import { FC, useMemo } from "react";
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Table } from "shared/app/Table";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Pencil02 } from "untitledui-js-base";
import { useNavigate } from "react-router-dom";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";
import { propertyGroupTitleMap } from "views/property-groups-configuration/constants";
import { formatTitle } from "utils/formatTitle";
import { plural } from "pluralize";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { getStateLabel } from "utils/stateCodes";
import { PropertyGroup } from "types/property-group";
import { PropertyGroupOptionBadgeList } from "shared/components/PropertyGroupOptionBadgeList";
import { Template } from "types/landlord/verificationTemplate";

interface PropertyGroupsTableProps {
  propertyGroups: PropertyGroup[];
  availableSplitOptions: PropertyGroupSplitBy[];
  searchTerm: string;
  isLoading: boolean;
  isUsingAppfolioPropertyGroups?: boolean;
}

export const PropertyGroupsTable: FC<PropertyGroupsTableProps> = ({
  availableSplitOptions,
  propertyGroups,
  searchTerm,
  isLoading,
  isUsingAppfolioPropertyGroups = false
}) => {
  const navigate = useNavigate();
  const dictionary = useUnitsPropertiesDictionary();

  const formatValue = (option: PropertyGroupSplitBy, value: string) => {
    if (option === PropertyGroupSplitBy.STATE) return getStateLabel(value);
    if (option === PropertyGroupSplitBy.CITY) return formatTitle(value);
    return value;
  };

  const columns = useMemo<ColumnDef<PropertyGroup>[]>(() => {
    // Don't show split option columns (State, City, Zip, Owner) in Appfolio mode
    const splitOptionColumns = isUsingAppfolioPropertyGroups
      ? []
      : availableSplitOptions.map((option, index) => ({
          id: `${option}`,
          size: 160,
          header: formatTitle(propertyGroupTitleMap[option]),
          cell: ({ row }) => {
            const splitDataItem = row.original.split_data_chain?.[index + 1];
            if (!splitDataItem) return null;

            const isOther = splitDataItem.is_other;
            const values = splitDataItem.split_values || [];

            const optionTitle = propertyGroupTitleMap[option] || option;

            return (
              <PropertyGroupOptionBadgeList
                values={values}
                formatValue={formatValue.bind(null, option)}
                isOther={isOther}
                otherTitle={plural(optionTitle)}
              />
            );
          }
        }));

    return [
      {
        id: "fixed_index",
        accessorKey: "index",
        header: "#",
        size: 30,
        cell: ({ row }) => (
          <div className="text-content-1000 font-medium">
            {row.original.index + 1}
          </div>
        )
      },
      {
        id: "name",
        accessorKey: "name",
        header: "Group name",
        cell: ({ row }) => {
          return (
            <p className="text-content-1000 font-medium">{row.original.name}</p>
          );
        }
      },
      ...splitOptionColumns,
      {
        id: "totalProperties fixed_width",
        accessorKey: "total_properties",
        header: `${dictionary.Properties}`,
        size: 50,
        cell: ({ row }) => (
          <div className="text-content-1000">
            {row.original.total_properties || 0}
          </div>
        )
      },
      {
        accessorKey: "verification_template",
        header: "Template",
        cell: ({ row }) => {
          const template = row.original.verification_template as
            | Template
            | undefined;
          return (
            <div className="text-content-1000 font-medium">
              {template?.name || "No template"}
            </div>
          );
        }
      },
      {
        id: "popover-action",
        size: 48,
        accessorFn: (row) => row.id,
        header: "",
        cell: (p) => {
          const id = p.getValue();
          return (
            <ContextMenuPopover
              contentClassName="pr-8"
              placement="bottom-end"
              actions={[
                {
                  title: (
                    <span className="flex gap-2 items-center">
                      <Pencil02 size="18" className="stroke-content-200" />
                      Edit details
                    </span>
                  ),
                  onClick: () => {
                    navigate(`/property-groups/${id}`);
                  }
                }
              ]}
            />
          );
        }
      }
    ];
  }, [availableSplitOptions]);

  const table = useReactTable({
    data: propertyGroups,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Table
      rowHeight="90px"
      emptyStateText={
        searchTerm
          ? `${dictionary.property} group will appear here`
          : `Add ${dictionary.property} groups to be able to invite applicants for screening`
      }
      emptyStateTitle={
        searchTerm ? "No matches found" : `No ${dictionary.property} groups`
      }
      isLoading={isLoading}
      onRowClick={(row) => navigate(`/property-groups/${row}`)}
      table={table}
    />
  );
};
